<template>
    <div>
        <div v-if="mutableAsset">
            <div class="mt-6 mb-4 flex flex-wrap justify-between items-center gap-4">
                <div>
                    <!--Page Title - Bound to the form for cool responsive update effect when name changes-->
                    <div class="flex items-start gap-2">
                        <h3 class="text-2xl font-medium leading-tight text-gray-800"
                            v-text="form.asset_name"></h3>
                        <badge v-if="mutableAsset.archived_at"
                               class="text-yellow-600 bg-yellow-100 rounded-md border border-yellow-300">Archived
                        </badge>
                        <model-replication-tooltip
                            v-if="mutableAsset.replication_details"
                            icon-class="w-4 h-4 text-gray-400"
                            :replication-details="
                                mutableAsset.replication_details">
                        </model-replication-tooltip>
                    </div>

                    <!--Breadcrumb Navigation-->
                    <nav class="hidden sm:flex" aria-label="Breadcrumb">
                        <ol role="list" class="flex items-center space-x-4">
                            <li>
                                <div>
                                    <a href="#" class="text-gray-400 hover:text-gray-500">
                                        <icon-home class="w-4 h-4"/>
                                        <span class="sr-only">Home</span>
                                    </a>
                                </div>
                            </li>
                            <li v-for="page in pages" :key="page.name">
                                <div class="flex items-center font-normal text-gray-400 hover:text-indigo-600">
                                    <span>/</span>
                                    <a :href="page.href"
                                       class="ml-3 text-xs whitespace-nowrap"
                                       :aria-current="page.current ? 'page' : undefined">{{ page.name }}
                                    </a>
                                </div>
                            </li>
                        </ol>
                    </nav>
                </div>

                <!--Options Menu-->
                <simple-dropdown label="Options">
                    <template slot="buttonOptions">
                        <simple-dropdown-option
                            value="Merge Asset"
                            key="merge_into_other_asset"
                            @click="showMergeModal">
                            Merge Into Other Asset
                        </simple-dropdown-option>
                    </template>
                    <template slot="buttonOptions">
                        <simple-dropdown-option
                            value="View Activity Log"
                            key="view_activity_log"
                            @click="navigateToActivityLog">
                            View Activity Log
                        </simple-dropdown-option>
                    </template>
                    <div class="border border-gray-200 my-2 w-full"></div>
                    <template slot="buttonOptions">
                        <simple-dropdown-option
                            value="Archive Asset"
                            key="archive"
                            @click="toggleArchivingModal">
                            {{ mutableAsset.archived_at === null ? "Archive" : "Unarchive" }} Asset
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </div>

            <!--Page Body Layout-->
            <div class="grid grid-cols-6 gap-8">
                <!--Main Details Form-->
                <div v-if="currentTab === 'details' || currentTailwindBreakpoint >= 3"
                     class="col-span-6 lg:col-span-2 order-1 lg:order-0">
                    <div class="bg-white rounded-md shadow p-6">

                        <!--Google Maps Image + Full Address-->
                        <div class="mb-4 rounded-md">
                            <iframe
                                :src="mutableAsset.address.google_maps_url"
                                class="rounded-md shadow-sm"
                                width="100%"
                                height="150"
                                allowfullscreen>
                            </iframe>

                            <h6 class="font-normal py-3 flex items-center justify-between gap-2 hover:text-indigo-600 cursor-pointer"
                                @click="editAddress(mutableAsset.address.id)">
                                <span>{{ mutableAsset.address.full_address }}</span>
                                <icon-pencil-alt class="h-5 w-5"></icon-pencil-alt>
                            </h6>
                        </div>

                        <!--Form Fields-->
                        <div class="grid grid-cols-2 gap-4">
                            <!--Asset Name-->
                            <div class="col-span-2">
                                <label class="font-medium" for="asset_name">Asset Name</label>
                                <input class="form-control"
                                       type="text"
                                       id="asset_name"
                                       name="asset_name"
                                       v-model="form.asset_name"
                                       :class="{ 'is-invalid': form.errors.has('asset_name'), }"/>

                                <span class="invalid-feedback" v-show="form.errors.has('asset_name')">
                                    {{ form.errors.get("asset_name") }}
                                </span>
                            </div>

                            <!--Portfolio Selection-->
                            <div class="col-span-2">
                                <label class="font-medium" for="portfolio">Portfolio</label>
                                <div id="portfolio">
                                    <el-select style="width: 100%"
                                               v-model="form.portfolio"
                                               placeholder="Select portfolio">
                                        <el-option
                                            v-for="item in app.state.user
                                                .portfolios"
                                            :key="item.id"
                                            :label="item.name"
                                            :value="item.id">
                                        </el-option>
                                    </el-select>
                                </div>
                                <span class="invalid-feedback"
                                      :class="{  'd-block': form.errors.has('portfolio'), }"
                                      v-show="form.errors.has('portfolio')">
                                    {{ form.errors.get("portfolio") }}
                                </span>
                            </div>

                            <!--Asset Group Selection-->
                            <div class="col-span-2">
                                <label class="font-medium" for="asset_group">Asset Group</label>
                                <div id="asset_group">
                                    <remote-select
                                        v-model="mutableAsset.asset_group"
                                        @input="handleAssetGroupChangeEvent"
                                        @focus="searchAssetGroups"
                                        :remote-method="searchAssetGroups"
                                        :data="assetGroups"
                                        :loading="loading"
                                        class="w-full"
                                        item-label="name"
                                        placeholder="Search Asset Groups">
                                        <template slot-scope="props">
                                            {{ props.item.name }}
                                        </template>
                                    </remote-select>
                                </div>
                                <span class="invalid-feedback"
                                      :class="{'d-block': form.errors.has('asset_group')}"
                                      v-show="form.errors.has('asset_group')">
                                    {{ form.errors.get("asset_group") }}
                                </span>
                            </div>

                            <!--Mandate Type Selection-->
                            <div class="col-span-2">
                                <label class="font-medium" for="portfolio">Mandate Type</label>
                                <div id="current_mandate_type">
                                    <el-select
                                        class="w-full"
                                        v-model="form.current_mandate_type"
                                        placeholder="Select Mandate Type"
                                        clearable>
                                        <el-option
                                            v-for="type in mandateTypes"
                                            :key="type"
                                            :label="mandateTypeLabel(type)"
                                            :value="type">
                                        </el-option>
                                    </el-select>
                                </div>
                                <span class="invalid-feedback"
                                      :class="{'d-block': form.errors.has('current_mandate_type')}"
                                      v-show="form.errors.has('current_mandate_type')">
                                    {{ form.errors.get("current_mandate_type") }}
                                </span>
                            </div>

                            <!--Notes Section-->
                            <div class="col-span-2">
                                <text-area
                                    input-label="Asset Notes"
                                    rows="5"
                                    v-model="form.notes"
                                ></text-area>
                            </div>

                            <!--Prevent external updates checkbox-->
                            <div class="col-span-2">
                                <checkbox class="font-medium flex" v-model="form.prevent_external_updates">
                                    <span class="ml-2 flex items-center select-none" title="">Prevent integrations from updating this record
                                        <span class="ml-2 pb-0.5"
                                              title="Prevent data syncing from external systems such as rams or payprop from overriding changes you make to this record.">
                                            <icon-help class="h-4 w-4"></icon-help>
                                        </span>
                                    </span>
                                </checkbox>
                            </div>

                            <div class="col-span-2 flex justify-end mt-8">
                                <button
                                    class="select-none px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                                    @click.prevent="updateAssetDetails"
                                    :disabled="form.busy">
                                    {{ form.busy ? "Saving..." : "Save" }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!--Navigation Tabs-->
                <div class="col-span-6 lg:col-span-4 order-0 lg:order-1">
                    <tabs
                        :tabs="tabs"
                        :selected-tab="currentTab"
                        @change-tab="setCurrentTab">
                        <template slot="floatRight">
                            <!--ShowArchived Toggle-->
                            <div class="flex items-center"
                                 v-if="currentTab === 'contacts'">
                                <span class="text-gray-500 font-medium text-xs mr-2">Show Archived</span>
                                <toggle
                                    v-model="showArchivedContacts"
                                    class="-mr-3"
                                ></toggle>
                            </div>
                        </template>
                    </tabs>

                    <!--Tab sections : Contacts-->
                    <div v-if="currentTab === 'contacts'">
                        <contacts-list-new
                            title="Landlords"
                            role="landlord"
                            contactable="App\Asset"
                            :contactable-id="assetId"
                            :uri="contactsUrl"
                            :show-archived-contacts="showArchivedContacts"
                            table-ref="contactsTable"
                            list-property="mutableContacts"
                        ></contacts-list-new>

                        <contacts-list-new
                            title="Tenants"
                            role="tenant"
                            contactable="App\Asset"
                            :contactable-id="assetId"
                            :uri="contactsUrl"
                            :show-archived-contacts="showArchivedContacts"
                            table-ref="contactsTable"
                            list-property="mutableContacts"
                        ></contacts-list-new>

                        <contacts-list-new
                            title="Other"
                            role="other"
                            contactable="App\Asset"
                            :contactable-id="assetId"
                            :uri="contactsUrl"
                            :show-archived-contacts="showArchivedContacts"
                            table-ref="contactsTable"
                            list-property="mutableContacts"
                        ></contacts-list-new>
                    </div>

                    <div v-if="currentTab === 'leases'">
                        <lease-list-for-asset
                            :assetId="assetId"
                            list-property="mutableLeases"
                            table-ref="leasesTable"
                        ></lease-list-for-asset>
                    </div>

                    <div v-if="currentTab === 'tickets'">
                        <ticket-list-for-asset
                            :assetId="assetId"
                            list-property="mutableTickets"
                            table-ref="ticketsTable"
                            :key="ticketListKey"
                            @show-create-ticket="toggleTicketCreateModal">
                        </ticket-list-for-asset>
                        <ticket-create-modal-base
                            :asset="mutableAsset"
                            :show="showTicketCreateModal"
                            @close-modal="toggleTicketCreateModal">
                        </ticket-create-modal-base>
                    </div>

                    <div v-if="currentTab === 'inspections'">
                        <inspection-list-for-asset
                            :assetId="assetId"
                            list-property="mutableInspections"
                            table-ref="ticketsTable"
                            :key="inspectionListKey"
                            @open-inspection-creation-modal="showCreateInspectionForm ">
                        </inspection-list-for-asset>
                    </div>
                </div>
            </div>

            <!--Modals-->
            <address-update-form
                :address="mutableAsset.address"
                @address-updated="handleAddressUpdatedEvent"
            ></address-update-form>
            <merge-property-modal
                :asset="mutableAsset"
                @asset-merged="hideMergeModal()"
            ></merge-property-modal>
        </div>

        <archive-and-unarchive-modal
            :show="showArchivingModal"
            :model-id="this.assetId"
            model-type="asset"
            :archiving="mutableAsset.archived_at === null"
            @actioned="afterArchivingAction"
            v-if="showArchivingModal"
            @close="toggleArchivingModal">
        </archive-and-unarchive-modal>

        <inspection-create-form
            v-if="mutableAsset"
            ref="inspectionCreateFormOnAsset"
            :asset="mutableAsset"
            :from-asset="true"
            @created-from-asset="handleInspectionCreated"
            :inspection-types="inspectionTypes">
        </inspection-create-form>

        <create-new-sms
            ref="smsCreateForm"
            contactable="App\Asset"
            :contactable-id="assetId"
            category="general">
        </create-new-sms>
    </div>
</template>

<script>
import Tabs from "../library/Tabs.vue";
import AssetGroupSearch from "../../mixins/asset-group-search";
import Address from "../../mixins/address";
import ShowAddressForm from "../address/address-update-form.vue";
import ContactsListNew from "../contactable/ContactListNew.vue";
import TicketListForAsset from "../tickets/TicketListForAsset.vue";
import InspectionListForAsset from "../inspections/InspectionListForAsset.vue";
import LeaseListForAsset from "../contact-groups/LeaseListForAsset.vue";
import archivingMixin from "../../mixins/archiving-mixin";
import TailwindCheckbox from "../library/TailwindCheckbox.vue";

export default {
    name: "view-asset-new",
    components: {
        TailwindCheckbox,
        LeaseListForAsset,
        TicketListForAsset,
        InspectionListForAsset,
        Tabs,
        ShowAddressForm,
        ContactsListNew,
    },
    mixins: [AssetGroupSearch, Address, archivingMixin],
    props: ["assetId"],

    created () {
        this.fetchAssetDetails();
        let self = this;

        Bus.$on("ticket-created", function () {
            // We increment the component key to force a rerender of the ticket list.
            self.ticketListKey += 1;
        });
    },

    mounted () {
        const initialTab =
            this.currentTailwindBreakpoint <= 0 ? "details" : "contacts";
        this.setCurrentTab(initialTab); // Use the setCurrentTab method to initialize.
    },

    data () {
        return {
            tabs: [
                {name: "Details", href: "#", current: false},
                {name: "Contacts", href: "#", current: false},
                {name: "Leases", href: "#", current: false},
                {name: "Tickets", href: "#", current: false},
                {name: "Inspections", href: "#", current: false},
            ],
            currentTab: null,
            contactsUrl: "/contacts/asset/" + this.assetId,
            showArchivedContacts: false,
            mutableAsset: null,
            mandateTypes: ["managed", "unmanaged", "unspecified"],
            pages: [
                {name: "Portfolio Data", href: "/assets", current: false},
                {name: "Assets", href: "/assets", current: true},
                {name: this.assetId, href: "/assets/" + this.assetId, current: true},
            ],
            form: {},
            showTicketCreateModal: false,
            inspectionListKey: 1,
            ticketListKey: 1,
            inspectionTypes: [],
        };
    },
    methods: {
        showMergeModal () {
            $("#merge-property-modal").modal("show");
        },
        hideMergeModal () {
            $("#merge-property-modal").modal("hide");
            this.reloadPage();
        },
        navigateToActivityLog () {
            const url = "/activity-log?id=" + this.assetId + "&type=Asset";
            window.open(url, "_blank");
        },
        afterArchivingAction () {
            if (this.mutableAsset.archived_at === null) {
                this.mutableAsset.archived_at = new Date();
            } else {
                this.mutableAsset.archived_at = null;
            }
        },
        reloadPage () {
            window.location.reload();
        },
        mandateTypeLabel (type) {
            return this.$options.filters.capitalize(type);
        },
        setCurrentTab (targetTabName) {
            this.currentTab = targetTabName;

            this.tabs.forEach((tab) => {
                tab.current =
                    tab.name.toLowerCase() === targetTabName.toLowerCase();
            });
        },
        initialiseForm () {
            this.form = new AppForm({
                asset_name: this.mutableAsset.name || "",
                portfolio: this.mutableAsset.portfolio?.id || null,
                asset_group: this.mutableAsset.asset_group?.id || null,
                current_mandate_type: this.mutableAsset.current_mandate_type || "unspecified",
                notes: this.mutableAsset.notes || "",
                prevent_external_updates: this.mutableAsset.prevent_external_updates,
            });
        },
        fetchAssetDetails () {
            axios.get(`/assets/${this.assetId}`).then((response) => {
                this.mutableAsset = response.data;
                this.initialiseForm();
            });
        },
        handleAssetGroupChangeEvent (assetGroup) {
            this.form.asset_group = assetGroup.id;
        },
        handleAddressUpdatedEvent (address) {
            this.$set(this.mutableAsset, "address", address);
        },
        updateAssetDetails () {
            App.patch("/assets/" + this.assetId + "/update", this.form)
            .then(() => {
                toastr.success("Asset details updated!");
            })
            .catch((error) => {
                toastr.error("Failed to update asset details!");
            });
        },
        toggleTicketCreateModal () {
            this.showTicketCreateModal = !this.showTicketCreateModal;
        },
        showCreateInspectionForm () {
            if (this.inspectionTypes.length <= 0) {
                this.fetchInspectionTypes();
            }
            this.$refs.inspectionCreateFormOnAsset.showInspectionCreateForm();
        },
        fetchInspectionTypes () {
            axios
            .get("/settings/inspections/inspection-types")
            .then((response) => (this.inspectionTypes = response.data))
            .catch((error) =>
                toastr.error(
                    "Could not fetch inspection types for filter. " + error
                )
            );
        },
        handleInspectionCreated () {
            // By incrementing the key we force the child component to re-render and reload the list.
            this.inspectionListKey += 1;
            this.$refs.inspectionCreateFormOnAsset.hideInspectionCreateForm();
        },
    },
};
</script>
